import { useCallback } from "react";
import { useWatch } from "react-hook-form";
import type { designLab } from "common/utils/models";
import { usePdpDeliveryEstimates, useCompositionForm } from "@design-lab/entities/composition";
import { usePrintOnDemandProducts } from "@design-lab/entities/product";
import { useUpdateDesignProject } from "../api/useUpdateDesignProject";

export function useUpdateWithDeliveryEstimates() {
  const updateProject = useUpdateDesignProject();
  const updateDeliveryEstimates = usePdpDeliveryEstimates();
  const { form: compositionForm } = useCompositionForm();

  // Watch the current composition
  const currentCompositionId = useWatch({
    control: compositionForm.control,
    name: "currentCompositionId"
  });

  const addedCompositions = useWatch({
    control: compositionForm.control,
    name: "addedCompositions"
  });

  // Get the current composition data
  const currentComposition = addedCompositions?.find(comp => comp.id === currentCompositionId);

  // Get product data for the current composition
  const catalogIds = currentComposition?.catalogId ? [currentComposition.catalogId] : [];
  const productsQueries = usePrintOnDemandProducts(catalogIds);
  const productData = productsQueries[0]?.data;

  const handleUpdateWithDeliveryEstimates = useCallback(
    async (updateParams: Parameters<typeof updateProject.mutate>[0]) => {
      try {
        // Build dtf_print array from the request payload
        const dtfPrint: designLab.getPdpDeliveryEstimates.DtfPrintItem[] = [];

        // Get the active composition ID and quantities
        const quantities = updateParams.payload.madeDesignProject?.quantities;

        // Calculate total quantity from ALL compositions
        let totalQuantity = 0;

        if (quantities) {
          Object.values(quantities).forEach((variants: any) => {
            Object.values(variants).forEach((variant: any) => {
              totalQuantity += variant.quantity || 0;
            });
          });
        }

        // Process placements from request payload to build dtf_print array
        if (updateParams.payload.madeDesignProjectPlacements) {
          const placements = updateParams.payload.madeDesignProjectPlacements;

          // Add front placement if exists
          if (placements["front"]?.dtfArtworkId) {
            const frontHeight = placements["front"].height;
            const frontWidth = placements["front"].width;

            if (frontHeight != null && frontWidth != null) {
              dtfPrint.push({
                artwork_id: parseInt(placements["front"].dtfArtworkId, 10),
                height: parseFloat(frontHeight.toString()),
                width: parseFloat(frontWidth.toString()),
                quantity: totalQuantity
              });
            }
          }

          // Add back placement if exists
          if (placements["back"]?.dtfArtworkId) {
            const backHeight = placements["back"].height;
            const backWidth = placements["back"].width;

            if (backHeight != null && backWidth != null) {
              dtfPrint.push({
                artwork_id: parseInt(placements["back"].dtfArtworkId, 10),
                height: parseFloat(backHeight.toString()),
                width: parseFloat(backWidth.toString()),
                quantity: totalQuantity
              });
            }
          }

          // Add frontLeftChest placement if exists (less common but possible)
          if (placements["frontLeftChest"]?.dtfArtworkId) {
            const chestHeight = placements["frontLeftChest"].height;
            const chestWidth = placements["frontLeftChest"].width;

            if (chestHeight != null && chestWidth != null) {
              dtfPrint.push({
                artwork_id: parseInt(placements["frontLeftChest"].dtfArtworkId, 10),
                height: parseFloat(chestHeight.toString()),
                width: parseFloat(chestWidth.toString()),
                quantity: totalQuantity
              });
            }
          }
        }

        // First, update the design project
        const result = await updateProject.mutateAsync(updateParams);

        // If successful and we have necessary data, fetch delivery estimates
        if (result && currentComposition && productData?.slug) {
          // Build variants hash from ALL compositions
          const variantsHash: Record<string, string> = {};
          let hasVariants = false;

          if (quantities) {
            // Loop through all compositions
            Object.values(quantities).forEach((compositionVariants: any) => {
              Object.entries(compositionVariants).forEach(
                ([variantId, variantData]: [string, any]) => {
                  const quantity = variantData.quantity || 0;
                  // If variant already exists, add to the quantity
                  const existingQuantity = variantsHash[variantId]
                    ? parseInt(variantsHash[variantId], 10)
                    : 0;
                  variantsHash[variantId] = String(existingQuantity + quantity);
                  if (quantity > 0) {
                    hasVariants = true;
                  }
                }
              );
            });
          }

          // Only trigger delivery estimates if we have both DTF print data and variants
          const hasDtfPrintData = dtfPrint.length > 0;
          const shouldTriggerDeliveryEstimates = hasDtfPrintData && hasVariants;

          if (shouldTriggerDeliveryEstimates) {
            // Get composition name (color name)
            const compositionName = currentComposition.colorName || "";

            // Trigger delivery estimates update
            await updateDeliveryEstimates.mutateAsync({
              slug: productData.slug,
              compositionName,
              variantsHash: JSON.stringify(variantsHash),
              preCut: false,
              dtfPrint
            });
          }
        }

        return result;
      } catch (error) {
        console.error("Error updating design project with delivery estimates:", error);
        window.Honeybadger?.notify("Error updating design project with delivery estimates", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            stack_trace: error instanceof Error ? error.stack : null,
            hook: "useUpdateWithDeliveryEstimates",
            catalogNumber: productData?.catalogNumber
          }
        });
        throw error;
      }
    },
    [updateProject, updateDeliveryEstimates, currentComposition, productData]
  );

  return {
    mutate: handleUpdateWithDeliveryEstimates,
    mutateAsync: handleUpdateWithDeliveryEstimates,
    isPending: updateProject.isPending || updateDeliveryEstimates.isPending,
    isSuccess: updateProject.isSuccess,
    isError: updateProject.isError || updateDeliveryEstimates.isError,
    error: updateProject.error || updateDeliveryEstimates.error,
    data: updateProject.data,
    status: updateProject.status,
    reset: () => {
      updateProject.reset();
      updateDeliveryEstimates.reset();
    }
  };
}
